(() => {
  // Currently open (panel, item) pair, tracked so the panel can follow its
  // tile on scroll/resize while anchored.
  let openPair = null;

  const closeAll = () => {
    document.querySelectorAll('.cg-qs.is-open').forEach((p) => {
      p.classList.remove('is-open', 'is-anchored');
      p.style.top = '';
      p.style.left = '';
    });
    document.querySelectorAll('[data-cg-item].is-active').forEach((i) => i.classList.remove('is-active'));
    openPair = null;
  };

  // Anchor the panel to its tile: below it when there is room, otherwise
  // above, otherwise beside it — always clamped to the viewport. Mobile
  // (<750px) keeps the CSS bottom-sheet placement.
  const positionPanel = (qs, item) => {
    if (!matchMedia('(min-width: 750px)').matches) return;
    qs.classList.add('is-anchored');
    const r = item.getBoundingClientRect();
    const pw = qs.offsetWidth;
    const ph = qs.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gap = 12;
    let left = r.left + r.width / 2 - pw / 2;
    let top;
    if (r.bottom + gap + ph <= vh - 16) {
      top = r.bottom + gap; // below the tile
    } else if (r.top - gap - ph >= 16) {
      top = r.top - gap - ph; // above the tile
    } else {
      // beside the tile, vertically centered on it
      left = r.right + gap + pw <= vw - 16 ? r.right + gap : r.left - gap - pw;
      top = r.top + r.height / 2 - ph / 2;
    }
    qs.style.left = Math.round(Math.min(Math.max(left, 16), vw - pw - 16)) + 'px';
    qs.style.top = Math.round(Math.min(Math.max(top, 16), vh - ph - 16)) + 'px';
  };

  let repositionRaf;
  const reposition = () => {
    if (!openPair) return;
    cancelAnimationFrame(repositionRaf);
    repositionRaf = requestAnimationFrame(() => {
      if (openPair) positionPanel(openPair.qs, openPair.item);
    });
  };
  addEventListener('scroll', reposition, true);
  addEventListener('resize', reposition);

  const getVariantId = (qs) =>
    qs.querySelector('.cg-qs__pill.is-on')?.dataset.variantId || qs.dataset.defaultVariantId;

  // Portal pattern: quick-shop panels are moved to <body> so that
  // position:fixed positions relative to the viewport instead of a
  // transformed ancestor (.cg-cat__item has a hover transform, which turns
  // it into the containing block for fixed-position descendants and clips
  // the panel at the tile's edges). Each item keeps a stable reference key
  // to its (now body-level) panel via data-cg-panel-ref / data-cg-panel-id.
  let cgPanelCounter = 0;
  const initQuickshopPortals = (root) => {
    (root || document).querySelectorAll('[data-cg-item]').forEach((item) => {
      if (item.dataset.cgPanelRef) return; // already portal-ized, idempotent
      const panel = item.querySelector('[data-cg-qs]');
      if (!panel) return;
      const key = 'qs-' + cgPanelCounter++;
      item.dataset.cgPanelRef = key;
      panel.dataset.cgPanelId = key;
      document.body.appendChild(panel);
    });
  };

  const toggleItemPanel = (item) => {
    const qs = document.querySelector('[data-cg-qs][data-cg-panel-id="' + item.dataset.cgPanelRef + '"]');
    if (!qs) return;
    const wasOpen = qs.classList.contains('is-open');
    closeAll();
    if (!wasOpen) {
      qs.classList.add('is-open');
      item.classList.add('is-active');
      positionPanel(qs, item);
      openPair = { qs, item };
    }
  };

  const getSectionInnerHTML = (html, selector) => {
    if (!html) return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  };

  // This vendored Dawn refreshes the cart drawer and header cart icon by
  // requesting their registered sections (theme/sections/cart-drawer.liquid,
  // theme/sections/cart-icon-bubble.liquid) alongside the /cart/add.js call
  // and splicing the returned markup into the existing DOM nodes — see
  // CartDrawer.renderContents in theme/assets/cart-drawer.js. We mirror that
  // exact pattern here (rather than a generic ?sections= fetch, which this
  // theme's global.js does not set up window.Shopify.routes.root for) so the
  // drawer contents and badge actually update.
  const refreshCart = (sections) => {
    document.dispatchEvent(new CustomEvent('cg:cart:added'));
    if (!sections) return;

    const cartDrawerRoot = document.querySelector('#CartDrawer');
    if (cartDrawerRoot && sections['cart-drawer']) {
      const inner = getSectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
      if (inner != null) cartDrawerRoot.innerHTML = inner;
    }

    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (cartIconBubble && sections['cart-icon-bubble']) {
      const inner = getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section');
      if (inner != null) cartIconBubble.innerHTML = inner;
    }

    document.querySelector('cart-drawer')?.classList.remove('is-empty');
  };

  document.addEventListener('click', async (e) => {
    const panel = e.target.closest('[data-cg-qs]');
    const item = e.target.closest('[data-cg-item]');

    if (!panel) {
      if (!item) { closeAll(); return; }
      if (e.target.closest('a')) return; // let real links navigate
      toggleItemPanel(item);
      return;
    }

    if (e.target.closest('[data-cg-close]')) { closeAll(); return; }

    const pill = e.target.closest('.cg-qs__pill');
    if (pill && !pill.disabled) {
      panel.querySelectorAll('.cg-qs__pill').forEach((p) => p.classList.remove('is-on'));
      pill.classList.add('is-on');
      const priceEl = panel.querySelector('[data-qs-price]');
      if (priceEl && pill.dataset.price) priceEl.textContent = pill.dataset.price;
      return;
    }

    const step = e.target.closest('[data-q]');
    if (step) {
      const counter = panel.querySelector('[data-qty]');
      counter.textContent = Math.max(1, parseInt(counter.textContent, 10) + parseInt(step.dataset.q, 10));
      return;
    }

    const variantId = getVariantId(panel);
    if (!variantId) return;
    const qty = parseInt(panel.querySelector('[data-qty]').textContent, 10);

    if (e.target.closest('[data-cg-buy]')) {
      const cartUrl = window.routes?.cart_url || '/cart';
      window.location.href = `${cartUrl}/${variantId}:${qty}`;
      return;
    }

    const addBtn = e.target.closest('[data-cg-add]');
    if (addBtn) {
      addBtn.disabled = true;
      const original = addBtn.textContent;
      try {
        const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: qty,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || `add.js ${res.status}`);
        addBtn.textContent = panel.dataset.labelAdded || 'Ajouté';
        refreshCart(data.sections);
      } catch (err) {
        addBtn.textContent = panel.dataset.labelRetry || 'Réessayer';
      } finally {
        setTimeout(() => { addBtn.disabled = false; addBtn.textContent = original; }, 1500);
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeAll(); return; }
    if (e.key === 'Enter' || e.key === ' ') {
      const el = document.activeElement;
      if (el && el.matches && el.matches('[data-cg-item]')) {
        e.preventDefault();
        toggleItemPanel(el);
      }
    }
  });

  // Script is deferred, so the DOM is already parsed by the time this runs.
  initQuickshopPortals();
  document.addEventListener('DOMContentLoaded', () => initQuickshopPortals());
  // Shopify theme editor re-renders sections in design mode; re-run the
  // portal init scoped to the newly-rendered section so its items get panels
  // moved to <body> too. Existing items are skipped via the idempotency guard.
  document.addEventListener('shopify:section:load', (e) => initQuickshopPortals(e.target));
})();
